import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaBarcode, FaTruck, FaCheck, FaBoxOpen } from 'react-icons/fa';
import styles from './styles/PickingPanel.module.css';
import { nombreOperativo } from '../../utils/productName';

interface OrderItem {
    Producto: string;
    Cantidad: number;
    Precio?: number;
    idProductos?: number;
}

interface PickingOrder {
    ID: string;
    Cliente: string;
    Direccion?: string;
    Telefono?: string;
    FechaEntrega: string;
    HorarioEntrega?: string;
    TipoPedido: string;
    Observaciones?: string;
    Items: OrderItem[];
    preparado: number;
}

interface Product {
    idProductos: number;
    NombresProductos: string;
    NombreEtiqueta?: string | null;
    barcode?: string;
    CodigoBarras?: number | string;
}

type FeedbackState = { type: 'ok' | 'warn' | 'err'; text: string } | null;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Key used to track scanning per item (idProductos if available, else Producto name)
const itemKey = (item: OrderItem) =>
    item.idProductos != null ? `id_${item.idProductos}` : `name_${item.Producto}`;

const PickingPanel: React.FC = () => {
    const [orders, setOrders] = useState<PickingOrder[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const [showPreparados, setShowPreparados] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<PickingOrder | null>(null);
    const [scannedCounts, setScannedCounts] = useState<Record<string, number>>({});
    const [lastScannedKey, setLastScannedKey] = useState<string | null>(null);
    const [scanInput, setScanInput] = useState('');
    const [feedback, setFeedback] = useState<FeedbackState>(null);
    const [flashClass, setFlashClass] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);

    const scanRef = useRef<HTMLInputElement>(null);

    // ── Fetch orders & products ──────────────────────────────────────────────
    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                tipo_tab: 'Envio',
                fecha_inicio: filterDate,
                fecha_fin: filterDate,
            });
            const res = await fetch(`${API_URL}/admin/orders?${params}`);
            if (res.ok) {
                const data: PickingOrder[] = await res.json();
                setOrders(data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [filterDate]);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    useEffect(() => {
        fetch(`${API_URL}/productos?include_hidden=true`)
            .then(r => r.json())
            .then((data: Product[]) => setProducts(data))
            .catch(console.error);
    }, []);

    // ── Select order → reset scanned counts ─────────────────────────────────
    const selectOrder = (order: PickingOrder) => {
        setSelectedOrder(order);
        const initial: Record<string, number> = {};
        order.Items.forEach(item => { initial[itemKey(item)] = 0; });
        setScannedCounts(initial);
        setLastScannedKey(null);
        setFeedback(null);
        setScanInput('');
        setTimeout(() => scanRef.current?.focus(), 50);
    };

    // ── Feedback flash ───────────────────────────────────────────────────────
    const showFeedback = (type: 'ok' | 'warn' | 'err', text: string) => {
        setFeedback({ type, text });
        setFlashClass(type === 'ok' ? styles['flash-ok'] : type === 'warn' ? styles['flash-warn'] : styles['flash-err']);
        setTimeout(() => { setFeedback(null); setFlashClass(''); }, 2500);
    };

    // ── Scan handler ─────────────────────────────────────────────────────────
    const handleScan = (raw: string) => {
        const value = raw.trim();
        if (!value || !selectedOrder) return;
        setScanInput('');

        // 1. Find product by barcode / id
        const product = products.find(p =>
            (p.barcode && p.barcode.toLowerCase() === value.toLowerCase()) ||
            (p.CodigoBarras != null && String(p.CodigoBarras) === value) ||
            String(p.idProductos) === value
        );

        if (!product) {
            showFeedback('err', `Código "${value}" no encontrado`);
            return;
        }

        // 2. Find matching item in order. El item del pedido guarda el nombre
        // operativo (etiqueta); el match fuerte es por idProductos.
        const nombreProd = nombreOperativo(product).toLowerCase();
        const matchingItem = selectedOrder.Items.find(item =>
            (item.idProductos != null && item.idProductos === product.idProductos) ||
            item.Producto.toLowerCase().includes(nombreProd) ||
            nombreProd.includes(item.Producto.toLowerCase())
        );

        if (!matchingItem) {
            showFeedback('warn', `${nombreOperativo(product)} no pertenece a este pedido`);
            return;
        }

        const key = itemKey(matchingItem);
        const current = scannedCounts[key] ?? 0;

        if (current >= matchingItem.Cantidad) {
            showFeedback('warn', `${matchingItem.Producto} ya completo (${matchingItem.Cantidad}/${matchingItem.Cantidad})`);
            return;
        }

        setScannedCounts(prev => ({ ...prev, [key]: prev[key] + 1 }));
        setLastScannedKey(key);
        showFeedback('ok', `${matchingItem.Producto} ✓ (${current + 1}/${matchingItem.Cantidad})`);
    };

    // Manual +1 button per item
    const handleManualAdd = (item: OrderItem) => {
        const key = itemKey(item);
        const current = scannedCounts[key] ?? 0;
        if (current >= item.Cantidad) return;
        setScannedCounts(prev => ({ ...prev, [key]: prev[key] + 1 }));
        setLastScannedKey(key);
    };

    // ── Mark as prepared ─────────────────────────────────────────────────────
    const handleMarcarPreparado = async (force = false) => {
        if (!selectedOrder) return;
        if (!force && !allComplete) {
            const ok = window.confirm('Hay ítems sin escanear completamente. ¿Marcar como preparado igual?');
            if (!ok) return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/admin/orders/${selectedOrder.ID}/preparado`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ preparado: 1 }),
            });
            if (res.ok) {
                await fetchOrders();
                // Update selected order state
                setSelectedOrder(prev => prev ? { ...prev, preparado: 1 } : null);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDesmarcar = async () => {
        if (!selectedOrder) return;
        setSubmitting(true);
        try {
            await fetch(`${API_URL}/admin/orders/${selectedOrder.ID}/preparado`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ preparado: 0 }),
            });
            await fetchOrders();
            setSelectedOrder(prev => prev ? { ...prev, preparado: 0 } : null);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Derived state ────────────────────────────────────────────────────────
    const totalUnits = selectedOrder
        ? selectedOrder.Items.reduce((s, i) => s + i.Cantidad, 0)
        : 0;
    const scannedUnits = selectedOrder
        ? selectedOrder.Items.reduce((s, i) => s + (scannedCounts[itemKey(i)] ?? 0), 0)
        : 0;
    const allComplete = totalUnits > 0 && scannedUnits === totalUnits;
    const progressPct = totalUnits > 0 ? (scannedUnits / totalUnits) * 100 : 0;

    const visibleOrders = orders.filter(o =>
        showPreparados ? true : o.preparado === 0
    );

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className={styles.container}>
            {/* ── Left panel ── */}
            <div className={styles.orderList}>
                <div className={styles.listHeader}>
                    <p className={styles.listTitle}>Pedidos con envío</p>
                    <div className={styles.filterRow}>
                        <input
                            type="date"
                            className={styles.dateInput}
                            value={filterDate}
                            onChange={e => setFilterDate(e.target.value)}
                        />
                        <button
                            className={`${styles.toggleBtn} ${showPreparados ? styles.active : ''}`}
                            onClick={() => setShowPreparados(p => !p)}
                            title="Mostrar preparados"
                        >
                            <FaCheck /> Ya listos
                        </button>
                    </div>
                </div>

                <div className={styles.orderCards}>
                    {loading && (
                        <div className={styles.emptyList}>Cargando...</div>
                    )}
                    {!loading && visibleOrders.length === 0 && (
                        <div className={styles.emptyList}>
                            No hay pedidos con envío para esta fecha.
                        </div>
                    )}
                    {visibleOrders.map(order => (
                        <div
                            key={order.ID}
                            className={`${styles.orderCard}
                                ${selectedOrder?.ID === order.ID ? styles.selected : ''}
                                ${order.preparado ? styles.preparado : ''}`}
                            onClick={() => selectOrder(order)}
                        >
                            <div className={styles.cardTop}>
                                <span className={styles.cardId}>#{order.ID}</span>
                                <span className={`${styles.cardBadge} ${order.preparado ? styles.badgeDone : styles.badgePending}`}>
                                    {order.preparado ? 'Listo' : 'Pendiente'}
                                </span>
                            </div>
                            <div className={styles.cardClient}>{order.Cliente}</div>
                            <div className={styles.cardMeta}>
                                <span><FaTruck /> {order.FechaEntrega}</span>
                                <span>{order.Items.length} ítem{order.Items.length !== 1 ? 's' : ''}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Right panel ── */}
            <div className={styles.workspace}>
                {!selectedOrder ? (
                    <div className={styles.emptyWorkspace}>
                        <FaBoxOpen />
                        <p>Seleccioná un pedido para comenzar el picking</p>
                    </div>
                ) : (
                    <>
                        {/* Order header */}
                        <div className={styles.orderHeader}>
                            <div className={styles.headerLeft}>
                                <h3>Pedido #{selectedOrder.ID} — {selectedOrder.Cliente}</h3>
                                <p>
                                    {selectedOrder.Direccion && <>{selectedOrder.Direccion}<br /></>}
                                    {selectedOrder.FechaEntrega}
                                    {selectedOrder.HorarioEntrega && ` · ${selectedOrder.HorarioEntrega}`}
                                    {selectedOrder.Telefono && <> · {selectedOrder.Telefono}</>}
                                    {selectedOrder.Observaciones && <><br />Obs: {selectedOrder.Observaciones}</>}
                                </p>
                            </div>
                            <div className={styles.headerProgress}>
                                <span className={styles.progressLabel}>
                                    {scannedUnits} / {totalUnits} unidades
                                </span>
                                <div className={styles.progressBar}>
                                    <div
                                        className={`${styles.progressFill} ${!allComplete && scannedUnits > 0 ? styles.partial : ''}`}
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Scanner input */}
                        <div className={styles.scanZone}>
                            <FaBarcode className={styles.scanIcon} />
                            <div className={styles.scanInputWrap}>
                                <input
                                    ref={scanRef}
                                    type="text"
                                    className={`${styles.scanInput} ${flashClass}`}
                                    placeholder="Escaneá el código de barras..."
                                    value={scanInput}
                                    onChange={e => setScanInput(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            handleScan(scanInput);
                                        }
                                    }}
                                    autoComplete="off"
                                    disabled={selectedOrder.preparado === 1}
                                />
                            </div>
                            {feedback && (
                                <span className={`${styles.scanFeedback}
                                    ${feedback.type === 'ok' ? styles.feedbackOk :
                                      feedback.type === 'warn' ? styles.feedbackWarn :
                                      styles.feedbackErr}`}>
                                    {feedback.text}
                                </span>
                            )}
                        </div>

                        {/* Items checklist */}
                        <div className={styles.itemsList}>
                            {selectedOrder.Items.map(item => {
                                const key = itemKey(item);
                                const scanned = scannedCounts[key] ?? 0;
                                const isComplete = scanned >= item.Cantidad;
                                const isPartial = scanned > 0 && !isComplete;
                                const isLast = lastScannedKey === key;

                                return (
                                    <div
                                        key={key}
                                        className={`${styles.itemRow}
                                            ${isComplete ? styles.complete : ''}
                                            ${isPartial ? styles.partial : ''}
                                            ${isLast ? styles.lastScanned : ''}`}
                                    >
                                        <span className={styles.itemStatus}>
                                            {isComplete ? '✅' : isPartial ? '🟡' : '⬜'}
                                        </span>
                                        <span className={styles.itemName}>{item.Producto}</span>
                                        <div className={styles.itemCounter}>
                                            <span className={`${styles.counterScanned}
                                                ${isComplete ? styles.complete :
                                                  isPartial ? styles.partial :
                                                  styles.zero}`}>
                                                {scanned}
                                            </span>
                                            <span className={styles.counterTotal}>/ {item.Cantidad}</span>
                                            {selectedOrder.preparado === 0 && (
                                                <button
                                                    className={styles.btnAddManual}
                                                    onClick={() => handleManualAdd(item)}
                                                    disabled={isComplete}
                                                    title="Agregar 1 manualmente"
                                                >
                                                    +1
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className={styles.workspaceFooter}>
                            {selectedOrder.preparado === 1 ? (
                                <>
                                    <div className={styles.alreadyDone}>
                                        <FaCheck /> Pedido marcado como preparado
                                    </div>
                                    <button
                                        className={styles.btnReset}
                                        onClick={handleDesmarcar}
                                        disabled={submitting}
                                    >
                                        Desmarcar
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        className={`${styles.btnPreparado} ${allComplete ? styles.ready : styles.incomplete}`}
                                        onClick={() => handleMarcarPreparado(allComplete)}
                                        disabled={submitting}
                                    >
                                        <FaCheck />
                                        {allComplete ? 'Marcar como preparado' : 'Marcar incompleto como preparado'}
                                    </button>
                                    <button
                                        className={styles.btnReset}
                                        onClick={() => selectOrder(selectedOrder)}
                                        title="Reiniciar escaneo"
                                    >
                                        Reiniciar
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PickingPanel;
